import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RevealSection, RevealCard } from '../Animations';
import { glyphFor } from '../ServiceGlyphs';

type ServiceItem = {
  id: string;
  title: string;
  description: string;
  features: string[];
};

type ServiceCardProps = { service: ServiceItem; index: number; key?: React.Key };

const ServiceCard = ({ service, index }: ServiceCardProps) => {
  const { id, title, description, features } = service;
  return (
    <Link
      to={`/services/${id}/`}
      className="group block h-full"
      aria-label={`Learn more about ${title}`}
    >
      <RevealCard className="relative h-full p-6 rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors duration-300 flex flex-col overflow-hidden">
        <span
          data-mono
          className="absolute top-8 right-8 text-[11px] text-zinc-500 tracking-[0.12em] uppercase z-10"
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {glyphFor(id)}

        <h3 className="text-xl font-semibold text-white mb-3 tracking-[-0.01em]">
          {title}
        </h3>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6 flex-grow">
          {description}
        </p>

        <ul className="space-y-2 mb-8 pt-6 border-t border-white/5">
          {features.map((f, i) => (
            <li key={i} className="flex items-center gap-2.5 text-sm text-zinc-400">
              <span className="w-1 h-1 rounded-full bg-[color:var(--color-accent)]" />
              {f}
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between text-sm font-medium text-white">
          <span>Learn more</span>
          <ArrowUpRight
            className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
            strokeWidth={1.75}
          />
        </div>
      </RevealCard>
    </Link>
  );
};

const Services = () => {
  const services: ServiceItem[] = [
    {
      id: 'ai-voice-agents',
      title: 'AI Voice Agents',
      description:
        'Intelligent AI voice assistants that handle your phone calls, book appointments, and answer customer questions 24/7. Never miss a lead again with our low-latency voice AI solutions.',
      features: ['Human-like conversation', '24/7 call handling', 'CRM integration'],
    },
    {
      id: 'n8n-workflow-automation',
      title: 'Workflow Automation',
      description:
        'We connect your entire tech stack using n8n and custom APIs. Eliminate manual data entry and human error with automated workflows that scale with your business.',
      features: ['n8n & Zapier experts', 'Custom API bridges', 'End-to-end automation'],
    },
    {
      id: 'custom-llm-training',
      title: 'Custom AI Assistants',
      description:
        'Private AI tools trained on your specific business data. Give your team a custom knowledge base that provides instant, accurate answers while keeping your data secure.',
      features: ['Private data training', 'Internal knowledge base', 'Custom GPT solutions'],
    },
  ];

  return (
    <section id="services" className="section-padding relative">
      <div className="container-custom">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12 md:mb-16">
          <RevealSection className="lg:col-span-7">
            <span className="eyebrow">01. Services</span>
            <div className="mt-3 mb-8 h-px bg-white/10" />
            <h2 className="text-[clamp(2rem,4.5vw,3.5rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.03em]">
              How we help you grow
            </h2>
          </RevealSection>
          <RevealSection className="lg:col-span-5">
            <p className="text-[17px] text-zinc-400 leading-[1.65] max-w-xl">
              We provide the technical expertise to automate your most time-consuming tasks,
              allowing you to focus on high-level strategy and growth.
            </p>
          </RevealSection>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {services.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
